import { Box, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import Hero from '@/components/Hero/Hero';
import { Card } from '@/components/LocationsList/LocationList.style';
import SeoHead from '@/components/SeoHead/SeoHead';
import CategoryCrossLinks from '@/components/StandorteContent/CategoryCrossLinks';
import CityContextBlock from '@/components/StandorteContent/CityContextBlock';
import DeliveryAreaSection from '@/components/StandorteContent/DeliveryAreaSection';
import FaqSection from '@/components/StandorteContent/FaqSection';
import PageMargins from '@/components/ui/PageMargins/PageMargins';
import {
  LOCATION_ROUTES,
  parseSlug,
  routeHandlers,
  validateSlugLength,
} from '@/helpers/locationPages';
import { getLocationBySlug, getLocations } from '@/helpers/strapi/helpers';
import {
  generateLocationFaqStructuredData,
  generateLocationServiceStructuredData,
  generateLogoStructuredData,
} from '@/helpers/strapi/structuredData';
import useFormatMessage from '@/hooks/useFormatMessage';
import DefaultLayout from '@/layouts/default';
import { renderTextContent } from '@/templates/Page/Page';

const CardGrid = dynamic(() => import('@/components/CardGrid/CardGrid'));
const PriceCatalogContainer = dynamic(() =>
  import('@/components/PriceCatalogContainer/PriceCatalogContainer')
);

function LocationCategoryPage({
  locationName,
  locationSlug,
  categoryName,
  categorySlug,
  categoryCards,
  priceCatalog,
  hero,
  textContent,
  seoText,
  seo,
  siblingLocations,
  locationState,
  cityData,
  categoryData,
  abPreisTag,
  abPriceBasis,
  allCategories,
}) {
  const pathname = usePathname();

  const formatMessage = useFormatMessage();

  return (
    <>
      <SeoHead title={seo?.title} description={seo?.description}>
        {generateLogoStructuredData(pathname)}
        {cityData &&
          categoryData &&
          generateLocationServiceStructuredData({
            city: locationName,
            bundesland: locationState,
            categoryName,
            partnerCount: cityData.partnerCount,
            abPreisTag,
          })}
        {categoryData &&
          abPreisTag &&
          generateLocationFaqStructuredData({
            city: locationName,
            categoryName,
            categorySingular: categoryData.categorySingular,
            articleDe: categoryData.articleDe,
            abPreisTag,
            priceBasis: abPriceBasis,
            priceFactor: categoryData.priceFactor,
          })}
      </SeoHead>
      <Hero
        headline={
          <Typography variant="h1" color="primary">
            {formatMessage('location_page_headline', {
              categoryName,
              locationName,
            })}
          </Typography>
        }
        pictures={hero}
      />
      {textContent && renderTextContent(textContent)}
      <PageMargins>
        {cityData && (
          <CityContextBlock
            locationName={locationName}
            partnerCount={cityData.partnerCount}
          />
        )}
        {categoryCards && (
          <CardGrid centerImage twoColumLayout cards={categoryCards} />
        )}
        {priceCatalog && <PriceCatalogContainer priceCatalog={priceCatalog} />}
        {seoText && (
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              bgcolor: 'grey.50',
              px: 3,
              py: 2,
              mt: 6,
            }}>
            <Typography
              variant="overline"
              color="primary"
              display="block"
              sx={{ mb: 0.5, lineHeight: 1.4 }}>
              {locationName} — Typische Einsatzbereiche
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {seoText}
            </Typography>
          </Box>
        )}
        {cityData && (
          <DeliveryAreaSection
            locationName={locationName}
            nearbyCities={cityData.nearbyCities}
            deliveryRadiusKm={cityData.deliveryRadiusKm}
            industryTags={cityData.industryTags}
          />
        )}
        {categoryData && abPreisTag && (
          <FaqSection
            categoryName={categoryName}
            categorySingular={categoryData.categorySingular}
            articleDe={categoryData.articleDe}
            locationName={locationName}
            abPreisTag={abPreisTag}
            priceBasis={abPriceBasis}
            priceFactor={categoryData.priceFactor}
          />
        )}
        {categorySlug && allCategories?.length > 0 && (
          <CategoryCrossLinks
            locationSlug={locationSlug}
            locationName={locationName}
            currentCategorySlug={categorySlug}
            allCategories={allCategories}
          />
        )}
      </PageMargins>
      {siblingLocations?.length > 0 && (
        <PageMargins sx={{ mt: 4, mb: 6 }}>
          <Card elevation={0}>
            <Typography variant="h6Redesign" color="primary.dark" gutterBottom>
              Weitere Standorte in {locationState}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {siblingLocations.map(loc => (
                <Link
                  key={loc.slug}
                  href={`${LOCATION_ROUTES.BASE_PATH}/${loc.slug}`}>
                  {loc.name}
                </Link>
              ))}
            </Box>
          </Card>
        </PageMargins>
      )}
    </>
  );
}

LocationCategoryPage.getLayout = function getLayout(page) {
  const breadcrumbs = page.props.breadcrumbs;
  return <DefaultLayout breadcrumbs={breadcrumbs}>{page}</DefaultLayout>;
};

export async function getServerSideProps({ params }) {
  try {
    const { slug } = params;

    const validationError = validateSlugLength(slug);
    if (validationError) {
      return validationError;
    }

    const {
      locationSlug,
      productCategorySlug,
      productFamilySlug,
      productGroupSlug,
    } = parseSlug(slug);

    const [location, allLocations] = await Promise.all([
      getLocationBySlug(locationSlug),
      getLocations(),
    ]);

    if (!location) {
      return { notFound: true };
    }

    const siblingLocations = allLocations
      .filter(loc => loc.state === location.state && loc.slug !== locationSlug)
      .map(loc => ({ name: loc.name, slug: loc.slug }));

    if (productGroupSlug) {
      return await routeHandlers.productGroup(
        productGroupSlug,
        productFamilySlug,
        productCategorySlug,
        location,
        siblingLocations
      );
    }

    if (productFamilySlug) {
      return await routeHandlers.productFamily(
        productFamilySlug,
        productCategorySlug,
        location,
        siblingLocations
      );
    }

    if (productCategorySlug) {
      return await routeHandlers.category(
        productCategorySlug,
        location,
        siblingLocations
      );
    }

    return await routeHandlers.location(location, siblingLocations);
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}

export default LocationCategoryPage;
